// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
// @ts-nocheck
import {
  Stack,
  Paper,
  Group,
  Text,
  Badge,
  SimpleGrid,
  Divider,
  Progress,
  Loader,
  Center,
} from '@mantine/core';
import { IconStar, IconUser, IconCalendar } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { formatDate } from '../../utils/format';
import { useReviewDetail } from '../../hooks/useHr';

interface ReviewRating {
  category: string;
  score: number;
  maxScore: number;
  comment: string | null;
}

interface ReviewDetail {
  id: string;
  employeeId: string;
  employeeName: string;
  reviewerId: string;
  reviewerName: string;
  reviewType: string;
  status: string;
  overallRating: number | null;
  dueDate: string;
  completedDate: string | null;
  comments: string | null;
  ratings: ReviewRating[];
}

function RatingStars({ rating, max = 5 }: { rating: number; max?: number }) {
  return (
    <Group gap={2}>
      {Array.from({ length: max }, (_: any, i) => (
        <IconStar
          key={i}
          size={16}
          fill={i < Math.round(rating) ? 'var(--mantine-color-yellow-5)' : 'none'}
          color={i < Math.round(rating) ? 'var(--mantine-color-yellow-5)' : 'var(--mantine-color-gray-4)'}
        />
      ))}
      <Text size="sm" fw={600} ml={4}>
        {rating.toFixed(1)}
      </Text>
    </Group>
  );
}

function ratingColor(score: number, maxScore: number): string {
  const pct = (score / maxScore) * 100;
  if (pct >= 80) return 'green';
  if (pct >= 60) return 'yellow';
  if (pct >= 40) return 'orange';
  return 'red';
}

function formatReviewType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: queryData, loading: isLoading } = useReviewDetail(id || '');
  const reviewData = queryData as { review?: ReviewDetail } | undefined;
  const review = reviewData?.review;

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!review) {
    return (
      <Stack gap="md">
        <PageHeader
          title="Review Not Found"
          breadcrumbs={[
            { label: 'HR', to: '/admin/workforce/hr/reviews' },
            { label: 'Reviews', to: '/admin/workforce/hr/reviews' },
            { label: 'Not Found' },
          ]}
        />
        <EmptyState
          icon={<IconStar size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="Review Not Found"
          message="The requested performance review could not be found."
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Performance Review"
        breadcrumbs={[
          { label: 'HR', to: '/admin/workforce/hr/reviews' },
          { label: 'Reviews', to: '/admin/workforce/hr/reviews' },
          { label: review.employeeName || review.employeeId },
        ]}
      />

      {/* Overview */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" ta="flex-start">
          <div>
            <Text fw={700} size="xl">
              {review.employeeName || review.employeeId}
            </Text>
            <Badge variant="light" size="sm" mt={4}>
              {formatReviewType(review.reviewType)}
            </Badge>
          </div>
          <StatusBadge status={review.status} />
        </Group>

        <Divider my="md" />

        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <div>
            <Group gap="xs">
              <IconUser size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                Reviewer
              </Text>
            </Group>
            <Text size="sm" fw={500}>
              {review.reviewerName || review.reviewerId}
            </Text>
          </div>
          <div>
            <Group gap="xs">
              <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                Due Date
              </Text>
            </Group>
            <Text size="sm" fw={500}>
              {formatDate(review.dueDate)}
            </Text>
          </div>
          {review.completedDate && (
            <div>
              <Group gap="xs">
                <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  Completed
                </Text>
              </Group>
              <Text size="sm" fw={500}>
                {formatDate(review.completedDate)}
              </Text>
            </div>
          )}
          {review.overallRating != null && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>
                Overall Rating
              </Text>
              <RatingStars rating={review.overallRating} />
            </div>
          )}
        </SimpleGrid>
      </Paper>

      {/* Category Ratings */}
      {review.ratings.length > 0 && (
        <Paper withBorder p="lg" radius="md">
          <Text fw={600} size="lg" mb="md">
            Category Ratings
          </Text>
          <Stack gap="md">
            {review.ratings.map((rating) => (
              <div key={rating.category}>
                <Group justify="space-between" mb={4}>
                  <Text size="sm" fw={500}>
                    {rating.category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </Text>
                  <Text size="sm" fw={600}>
                    {rating.score} / {rating.maxScore}
                  </Text>
                </Group>
                <Progress
                  value={(rating.score / rating.maxScore) * 100}
                  color={ratingColor(rating.score, rating.maxScore)}
                  size="md"
                  radius="xl"
                />
                {rating.comment && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {rating.comment}
                  </Text>
                )}
              </div>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Comments */}
      {review.comments && (
        <Paper withBorder p="lg" radius="md">
          <Text fw={600} size="lg" mb="md">
            Comments
          </Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {review.comments}
          </Text>
        </Paper>
      )}
    </Stack>
  );
}
